import { fetchCart } from '@http/endpoints'
import { PRODUCT_DEAL_TYPE } from '@tracking'

import { mapFromCart } from '../modules/common'

import address from './address'
import cart from './cart'
import discount from './discount'
import loader from './loader'
import payment from './payment'
import swap from './swap'
import userInformation from './userInformation'

/**
 * Namespaced Checkout Vuex Store
 * Read more: https://github.com/BackMarket/front/wiki/%5BRFC%5D-Checkout-Vuex-store
 */
export default {
  namespaced: true,

  modules: {
    address,
    cart,
    discount,
    loader,
    payment,
    swap,
    userInformation,
  },

  /**
   * @typedef {Object} State
   * @property {boolean} isLoaded
   * @returns {State}
   */
  state: () => ({
    isLoaded: false,
  }),

  mutations: {
    /**
     * @param {State} state
     * @param {boolean} isLoaded
     */
    setIsLoaded(state, isLoaded) {
      state.isLoaded = isLoaded
    },
  },

  getters: {
    /**
     * @param {State} state
     */
    isLoaded: (state) => state.isLoaded,

    /**
     * @param {Object} state
     * @param {Object} getters
     * @returns {Price}
     */
    priceAfterDiscount: (state, getters) =>
      getters['discount/isApplied']
        ? getters['discount/deductedPrice']
        : getters['cart/price'],

    /**
     * @param {Object} state
     * @param {Object} getters
     * @returns {boolean} Returns true if the current checkout state is shippable.
     * For regular shipping:
     * - shipping address is valid (see address/isShippingAddressComplete)
     * - cart items are shippable for the current shipping address country (see
     *   cart/isShippableForCountry)
     * For collection point shipping:
     * - customer details are valid (see address/isCollectionPointAddressComplete)
     * - note: no check is performed for the selected collection point, that is
     *         a separate check, which is used for a different redirection (see
     *         cart/isSelectedCollectionPointMissing).
     */
    isShippable: (state, getters) => {
      const isShippable = getters['address/hasCollectionPoint']
        ? getters['address/isCollectionPointAddressComplete']
        : getters['address/isShippingAddressComplete'] &&
          getters['cart/isShippableForCountry'](
            getters['address/shipping'].country,
          )

      return isShippable && getters['address/isBillingAddressComplete']
    },

    /**
     * @param {Object} state
     * @param {Object} getters
     * @returns {Object}
     */
    trackingData: (state, getters) => ({
      products: getters['cart/items'].map((item) => ({
        ...item.productTrackingData,
        dealType: PRODUCT_DEAL_TYPE.NORMAL,
        splitPayment: getters['payment/advertisedMethodsId'],
        isInsuranceEligible: !!item.insuranceOffers.length,
      })),
      swap: getters['swap/hasOffer'],
    }),
  },

  actions: {
    /**
     * @param {import('vuex').ActionContext<void, Object>} context
     * @param {Object} [payload]
     * @return {Promise<void>}
     */
    async fetch({ getters, commit, dispatch }) {
      const queryParams = {}

      // 1. Fetch cart & addresses
      const { payload } = await dispatch(
        'http/request',
        { request: fetchCart, queryParams },
        { root: true },
      )
      // TODO [PI-2154] Migrate to Checkout API
      const sanitizedPayload = mapFromCart(payload)
      await Promise.all([
        dispatch('cart/set', sanitizedPayload),
        dispatch('address/set', sanitizedPayload),
        dispatch('payment/set', sanitizedPayload),
        dispatch('swap/set', sanitizedPayload),
        dispatch('userInformation/set', sanitizedPayload),
      ])

      // 2. Fetch payment methods
      await dispatch('fetchPaymentMethods')

      // 3. Update discount store
      await dispatch('discount/setBasePrice', getters['cart/price'])

      commit('setIsLoaded', true)
    },

    /**
     * @private
     * @param {Object} context
     */
    async fetchPaymentMethods({ dispatch, getters }) {
      await dispatch('payment/fetchMethods', {
        cartItemsIds: getters['cart/itemsIds'],
        cartPrice: getters.priceAfterDiscount,
      })
    },

    /**
     * @param {Object} context
     * @param {Object} payload
     * @param {Object} payload.paymentMethod
     */
    async setPaymentMethod({ dispatch }, { paymentMethod }) {
      await dispatch('payment/setSelectedMethod', { paymentMethod })
      await dispatch('discount/setPaymentMethod', { paymentMethod })
    },

    /**
     * @param {Object} context
     * @param {any} payload See `discount/checkToken`
     */
    async checkDiscountToken({ dispatch, getters }, payload) {
      await dispatch('discount/checkToken', payload)

      if (getters['discount/isApplied']) {
        await dispatch('fetchPaymentMethods')
      }
    },

    /**
     * @param {Object} context
     * @param {any} payload See `discount/reset`
     */
    async resetDiscount({ dispatch }, payload) {
      await dispatch('discount/reset', payload)
      await dispatch('fetchPaymentMethods')
    },
  },
}
